import {
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TableContainer,
  Table,
  TableRow,
  TableCell,
  TableBody,
  Tab,
  Box,
  IconButton,
  Collapse,
  TableHead,
  Divider,
  Tooltip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import './PreviousRequestModal.css';
import { useState } from 'react';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Proposal } from '../../models/Proposal';
import Syllabus, { SuggestedSyllabus } from '../../models/Syllabus';
import Institution from '../../models/Institution';
import Activity from '../../models/Activity';
import { Cardinality } from '../../models/Cardinality';
import { getsimulationActivityIcon } from '../../application/Request/TargetActivitiesTable/tableActivitiesUtils';
import { ErrorOutline, LaunchRounded} from '@mui/icons-material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import STEPS from '../../application/Request/Request/STEPS';
import { useAppContext } from '../../application/App.Context';
import { useRequestType } from '../../shared/hooks/useRequestType';

export interface ActivityMinified {
  name?: string;
  id?: string;
  academic_transcript?: {
    calification?: string;
    approval_date?: string;
    folder?: string;
    book?: string;
  };
}
interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: (isConfirmed: boolean) => void;
  studentData: {
    name?: string;
    lastName?: string;
    documentType?: string;
    documentNumber?: string;
    email?: string;
    originInstitution?: string;
    originPlan?: string;
    targetCareer?: string;
  } | null;
  academicHistory?: {
    sourceInstitution?: string;
    sourceSyllabus?: string;
    sourceActivities?: ActivityMinified[];
  },
  proposals?: Proposal[];
  targetSyllabus?: Syllabus | SuggestedSyllabus | null;
  targetInstitution?: Institution | null;
  targetActivities?: Activity[],
  cardinality?: Cardinality[];
  allActivitiesWasEnriched?: boolean;
  termsAndConditionsChecked?: boolean;
  hasStudyPlanAndAcademicReport?: boolean;
  thirdStepValidation?: boolean;
  handleStepForStepper: (step: number) => void;
}

const ConfirmationModal = ({
  isOpen,
  onClose,
  studentData,
  academicHistory,
  proposals,
  targetSyllabus,
  targetInstitution,
  targetActivities,
  cardinality,
  allActivitiesWasEnriched,
  termsAndConditionsChecked,
  hasStudyPlanAndAcademicReport,
  thirdStepValidation,
  handleStepForStepper
}: ConfirmationModalProps) => {
  const { t } = useTranslation();

  const handleCloseConfirmationModal = (
    _event: React.SyntheticEvent<Element, Event>,
    reason: string
  ) => {
    if (reason && (reason === 'backdropClick' || reason === 'escapeKeyDown'))
      return;
    onClose(false);
  };
  const { user } = useAppContext();
  const { isInternal, isExternal } = useRequestType();
  const [value, setValue] = useState('1');
  const [openSourceActivities, setOpenSourceActivities] = useState(true);
  const [openEquivalencies, setOpenEquivalencies] = useState(false);
  const [openProposals, setOpenProposals] = useState(false);


  const findUnmatchedActivities = (sourceActivities?: ActivityMinified[], rules?: Cardinality[], proposals?: Proposal[]) => {
    if (!sourceActivities) return []

    const ruleActivityIds = new Set();

    rules?.forEach(rule => {
      rule.source_activities.forEach(activity => {
        ruleActivityIds.add(activity.id);
      });
    });
    const proposalActivityIds = new Set();
    proposals?.forEach(proposal => {
      if (proposal.sourceActivityId) {
        proposalActivityIds.add(proposal.sourceActivityId);
      }
    });

    const unmatched = sourceActivities.filter(activity =>
      !ruleActivityIds.has(activity.id) &&
      !proposalActivityIds.has(activity.id)
    );

    return unmatched;
  }

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  const closeAndMoveToStep = (step: number) => {
    onClose(false)
    handleStepForStepper(step);
  }

  const iconRedirectionToStep = (step: number) => {
    const stepToString = (step + 1).toString()
    return <Tooltip placement='top' arrow title={t('confirmationRedirectTitle') + ' ' + stepToString} color='primary'>
      <Button onClick={() => closeAndMoveToStep(step)} sx={{ minWidth: '0px', padding: '0px' }} data-testid={`back-step-${stepToString}`}>
        <LaunchRounded sx={{ marginLeft: '5px' }} />
      </Button>
    </Tooltip>
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleCloseConfirmationModal}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullWidth
      maxWidth="sm"
      scroll="paper"
    >
      <DialogContent>
        <div
          id="alert-dialog-description"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            color: 'black'
          }}
        >
          <Typography fontSize="20px" marginBottom="14px" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
            {t('confirmationTitle')}
            {!termsAndConditionsChecked &&
              <Tooltip placement='top' arrow title={t('confirmationDialogCheckTermsAndConditionsTitle')} data-testid={`term-and-condition-alert`}>
                <ErrorOutline sx={{ marginLeft: '5px' }} color='warning' />
              </Tooltip>}
            {!termsAndConditionsChecked && iconRedirectionToStep(STEPS.DOCUMENT_UPLOAD)}
          </Typography>
          <Typography marginBottom="10px">
            {t('confirmationSubtitle')}
          </Typography>
          <Box sx={{ width: '100%', typography: 'body1' }}>
            <TabContext value={value}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <TabList onChange={handleChange}>
                  <Tab label={t('stepOneName')} value="1" data-testid='step-one-confirm-request'/>
                  <Tab label={t('stepTwoName')} value="2" data-testid='step-two-confirm-request'/>
                  <Tab label={t('stepThreeName')} value="3" data-testid='step-three-confirm-request'/>
                  <Tab label={t('stepFourName')} value="4" data-testid='step-four-confirm-request'/>
                  <Tab label={t('stepFiveName')} value="5" data-testid='step-five-confirm-request'/>
                </TabList>
              </Box>
              <TabPanel value="1">
                <TableContainer className="REQXXI-Previus-TableBody">
                  <Table
                    stickyHeader
                    size="small"
                    sx={{ minWidth: 500 }}
                    aria-label="customized table"
                  >
                    <TableBody>
                      <TableRow>
                        <TableCell>{t('nameLabel')}</TableCell>
                        <TableCell>
                          {studentData?.name} {studentData?.lastName}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>{studentData?.documentType}</TableCell>
                        <TableCell sx={{ display: 'flex', alignItems: 'center' }}>
                          {studentData?.documentNumber}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>{t('emailLabel')}</TableCell>
                        <TableCell>
                          {studentData?.email}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                <Divider sx={{ marginTop: '15px' }} />
                <div style={{ display: 'flex', alignItems: 'center', marginTop: '10px' }}>
                  <Typography color="#949494" sx={{ fontSize: '14px' }}>
                    {t('editLabelCapitalize') + ' ' + t('stepOneName')}
                  </Typography>
                  {iconRedirectionToStep(STEPS.STUDENT_DATA)}
                </div>
              </TabPanel>
              <TabPanel value="2">
                <Table
                  stickyHeader
                  size="small"
                  aria-label="customized table"
                >
                  <TableBody>
                    <TableRow>
                      <TableCell>{t('sourceInstitution')}</TableCell>
                      <TableCell>
                        {academicHistory?.sourceInstitution}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {t('sourceSyllabus')}
                        </div>
                      </TableCell>
                      <TableCell>
                        {academicHistory?.sourceSyllabus}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <div style={{ display: 'flex', alignItems: 'center', marginTop: '10px' }}>
                  <Typography color="#949494" sx={{ fontSize: '14px' }}>
                    {t('editLabelCapitalize') + ' ' + t('stepTwoName')}
                  </Typography>
                  {iconRedirectionToStep(STEPS.SIMULATION)}
                </div>
              </TabPanel>
              <TabPanel value="3">
                <Table>
                  <TableBody>
                  <TableRow>
                      <div style={{ width: 'max-content', fontSize: '0.875rem', fontWeight: 400, padding: '6px 5px', borderBottom: '1px solid rgba(224, 224, 224, 1)', alignItems: 'center', display: 'flex' }}>
                        <IconButton
                          aria-label="expand row"
                          size="small"
                          data-testid="expand-Source-activities"
                          onClick={() => setOpenSourceActivities(!openSourceActivities)}
                          sx={{ marginRight: '5px' }}
                        >
                          {openSourceActivities ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                        {t('sourceTableTitle')}
                        {!allActivitiesWasEnriched &&
                          <Tooltip placement='top' arrow title={t('confirmationDialogCompleteOriginActivitiesTitle')}>
                            <ErrorOutline sx={{ marginLeft: '5px' }} color='warning' />
                          </Tooltip>}
                        {!allActivitiesWasEnriched && iconRedirectionToStep(STEPS.ACADEMIC_HISTORY)}
                      </div>
                      <TableCell sx={{ backgroundColor: '#FFF !important' }}></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                  {academicHistory?.sourceActivities?.map((activity: any) => (
                    <Collapse in={openSourceActivities} timeout="auto" unmountOnExit key={activity.id}>
                      <Box sx={{ margin: 1 }}>
                        <Typography gutterBottom component="div">
                          {activity?.name}
                        </Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell><Typography sx={{ color: 'black', fontSize: '0.875rem', }}>{t('calification')}</Typography></TableCell>
                              <TableCell><Typography sx={{ color: 'black', fontSize: '0.875rem', }}>{t('approvedDate')}</Typography></TableCell>
                              <TableCell><Typography sx={{ color: 'black', fontSize: '0.875rem', }}>{t('folder')}</Typography></TableCell>
                              <TableCell><Typography sx={{ color: 'black', fontSize: '0.875rem', }}>{t('book')}</Typography></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell>{activity?.academic_transcript?.calification ??
                                <Tooltip placement='top' arrow title={t('loadData')}>
                                  <ErrorOutline sx={{ marginLeft: '5px' }} color='warning' />
                                </Tooltip>}
                              </TableCell>
                              <TableCell>{activity?.academic_transcript?.approval_date ??
                                <Tooltip placement='top' arrow title={t('loadData')}>
                                  <ErrorOutline sx={{ marginLeft: '5px' }} color='warning' />
                                </Tooltip>}</TableCell>
                              <TableCell>{activity?.academic_transcript?.folder ??
                                <Tooltip placement='top' arrow title={t('loadData')}>
                                  <ErrorOutline sx={{ marginLeft: '5px' }} color='warning' />
                                </Tooltip>}</TableCell>
                              <TableCell>{activity?.academic_transcript?.book ??
                                <Tooltip placement='top' arrow title={t('loadData')}>
                                  <ErrorOutline sx={{ marginLeft: '5px' }} color='warning' />
                                </Tooltip>}</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                        <Divider sx={{ marginTop: '15px' }} />
                      </Box>
                    </Collapse>
                  ))}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginTop: '10px' }}>
                  <Typography color="#949494" sx={{ fontSize: '14px' }}>
                    {t('editLabelCapitalize') + ' ' + t('stepThreeName')}
                  </Typography>
                  {iconRedirectionToStep(STEPS.ACADEMIC_HISTORY)}
                </div>
              </TabPanel>
              <TabPanel value="4">
                <Table
                  stickyHeader
                  size="small"
                  aria-label="customized table"
                >
                  <TableBody>
                    <TableRow>
                      <TableCell>{t('targetSyllabus')}</TableCell>
                      <TableCell>
                        {targetSyllabus?.name ?? <Tooltip placement='top' arrow title={t('confirmationDialogSelectTargetSyllabusTitle')}>
                          <ErrorOutline sx={{ marginLeft: '5px' }} color='warning' />
                        </Tooltip>}
                      </TableCell>
                      {!targetSyllabus?.name && iconRedirectionToStep(STEPS.TARGET_SELECTION)}
                    </TableRow>
                    {cardinality && cardinality.length > 0 &&
                      <TableRow>
                        <div style={{ width: 'max-content', fontSize: '0.875rem', fontWeight: 400, padding: '6px 5px', borderBottom: '1px solid rgba(224, 224, 224, 1)' }}>
                          <IconButton
                            aria-label="expand row"
                            size="small"
                            id="expand-Equivalencies"
                            data-testid="expand-Equivalencies"
                            onClick={() => {
                              setOpenEquivalencies(!openEquivalencies)
                              setOpenProposals(false)
                            }}
                            sx={{ marginRight: '5px' }}
                          >
                            {openEquivalencies ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                          {t('equivalencyRequestListSimulate')}
                        </div>
                        <TableCell sx={{ backgroundColor: '#FFF !important' }}></TableCell>
                      </TableRow>}
                  </TableBody>
                </Table>
                {cardinality?.length === 0 && proposals?.length === 0 && (user?.valid && isInternal) &&
                  <Table
                    stickyHeader
                    size="small"
                    aria-label="customized table"
                  >
                    <TableBody>
                      <TableRow>
                        <div style={{ fontSize: '0.875rem', fontWeight: 400, padding: '6px 5px', borderBottom: '1px solid rgba(224, 224, 224, 1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {t('emptyEquivalencies')}
                        </div>
                      </TableRow>
                    </TableBody>
                  </Table>
                }
                {cardinality && cardinality.length > 0 &&
                  <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                    {cardinality?.map((c: Cardinality) => (
                      <Collapse in={openEquivalencies} timeout="auto" unmountOnExit key={c.id}>
                        <Box sx={{ margin: 1 }}>
                          <Typography variant="h6" gutterBottom component="div">
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell><Typography fontSize={14} sx={{ color: 'black' }}>{academicHistory?.sourceInstitution}</Typography></TableCell>
                                <TableCell><Typography fontSize={14} sx={{ color: 'black' }}>{targetInstitution?.name}</Typography></TableCell>
                                <TableCell><Typography fontSize={14} sx={{ color: 'black' }}>{t('state')}</Typography></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              <TableRow>
                                <TableCell >
                                  {c.source_activities?.map((activity: any) => (
                                    <div key={activity.id}>
                                      {activity.name}
                                    </div>
                                  ))}
                                </TableCell>
                                <TableCell>{c.target_activities?.map((activity: any) => (
                                  <div key={activity.id}>
                                    {activity.name}
                                  </div>
                                ))}</TableCell>
                                <TableCell>{getsimulationActivityIcon(c.state, t)}</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                          <Divider sx={{ marginTop: '15px' }} />
                        </Box>
                      </Collapse>
                    ))}
                  </div>}
                {((proposals && proposals.length > 0) || (findUnmatchedActivities(academicHistory?.sourceActivities, cardinality, proposals).length > 0 && isExternal)) &&
                  <>
                    <Table
                      stickyHeader
                      size="small"
                      aria-label="customized table"
                    >
                      <TableBody>
                        <TableRow>
                          <div style={{ fontSize: '0.875rem', fontWeight: 400, padding: '6px 5px', borderBottom: '1px solid rgba(224, 224, 224, 1)', display: 'flex', alignItems: 'center' }}>
                            <IconButton
                              aria-label="expand row"
                              size="small"
                              id="expand-proposals"
                              data-testid="expand-proposals"
                              onClick={() => {
                                setOpenProposals(!openProposals)
                                setOpenEquivalencies(false)
                              }}
                              sx={{ marginRight: '5px' }}
                              disabled={!proposals || proposals.length === 0}
                            >
                              {openProposals ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            </IconButton>
                            {t('proposalsTitle')}
                            {findUnmatchedActivities(academicHistory?.sourceActivities, cardinality, proposals).length > 0 && targetSyllabus?.name && (
                              isInternal && (
                                <Tooltip placement='top' arrow title={t('confirmationDialogUserValidTitle')}>
                                  <ErrorOutline sx={{ marginLeft: '5px' }} color='warning' />
                                </Tooltip>
                              )
                            )}

                            {findUnmatchedActivities(academicHistory?.sourceActivities, cardinality, proposals).length > 0 && targetSyllabus?.name && (
                              isExternal && (
                                <Tooltip placement='top' arrow title={t('confirmationDialogCompleteProposalsTitle')}>
                                  <ErrorOutline sx={{ marginLeft: '5px' }} color='warning' />
                                </Tooltip>
                              )
                            )}

                            {findUnmatchedActivities(academicHistory?.sourceActivities, cardinality, proposals).length > 0 && iconRedirectionToStep(STEPS.TARGET_SELECTION)}
                          </div>
                          <TableCell sx={{ backgroundColor: '#FFF !important' }}></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                    <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                      {proposals?.map((proposal: Proposal) => (
                        <Collapse in={openProposals} timeout="auto" unmountOnExit  key={proposal?.proposalId}>
                          <Box sx={{ margin: 1 }}>
                            <Typography variant="h6" gutterBottom component="div">
                            </Typography>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell><Typography fontSize={14} sx={{ color: 'black' }}>{academicHistory?.sourceInstitution}</Typography></TableCell>
                                  <TableCell><Typography fontSize={14} sx={{ color: 'black' }}>{targetInstitution?.name}</Typography></TableCell>
                                  <TableCell><Typography fontSize={14} sx={{ color: 'black' }}>{t('state')}</Typography></TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                <TableRow>
                                  <TableCell >
                                    {academicHistory?.sourceActivities?.find((activity) => activity.id === proposal.sourceActivityId)?.name}
                                  </TableCell>
                                  <TableCell>{targetActivities?.find((activity) => activity.id === proposal.targetActivityId)?.name}</TableCell>
                                  <TableCell>{getsimulationActivityIcon(proposal.state, t)}</TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                            <Divider sx={{ marginTop: '15px' }} />
                          </Box>
                        </Collapse>
                      ))}
                    </div></>}
                <div style={{ display: 'flex', alignItems: 'center', marginTop: '10px' }}>
                  <Typography color="#949494" sx={{ fontSize: '14px' }}>
                    {t('editLabelCapitalize') + ' ' + t('stepFourName')}
                  </Typography>
                  {iconRedirectionToStep(STEPS.TARGET_SELECTION)}
                </div>
              </TabPanel>
              <TabPanel value="5">
                <TableContainer className="REQXXI-Previus-TableBody">
                  <Table
                    stickyHeader
                    size="small"
                    sx={{ minWidth: 500 }}
                    aria-label="customized table"
                  >
                    <TableBody>
                      <TableRow>
                        <TableCell>{t('du_front')}</TableCell>
                        <TableCell>
                          {(isExternal && !hasStudyPlanAndAcademicReport) ?
                            <Tooltip placement='top' arrow title={t('confirmationDialogCompleteDu')} data-testid='complete-du-front-warning'>
                              <ErrorOutline sx={{ marginLeft: '5px' }} color='warning' />
                            </Tooltip>
                            :
                            <Tooltip placement='top' arrow title={t('confirmationDialogCheckAcademicReportTitleSuccess')} data-testid='complete-du-front-success'>
                              <CheckCircleOutlineIcon sx={{ marginLeft: '5px' }} color='success' />
                            </Tooltip>
                          }
                          {!hasStudyPlanAndAcademicReport && iconRedirectionToStep(STEPS.DOCUMENT_UPLOAD)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>{t('du_back')}</TableCell>
                        <TableCell>
                          {(isExternal && !hasStudyPlanAndAcademicReport) ?
                            <Tooltip placement='top' arrow title={t('confirmationDialogCompleteDu')} data-testid='complete-du-back-warning'>
                              <ErrorOutline sx={{ marginLeft: '5px' }} color='warning' />
                            </Tooltip>
                            :
                            <Tooltip placement='top' arrow title={t('confirmationDialogCheckAcademicReportTitleSuccess')} data-testid='complete-du-back-success'>
                              <CheckCircleOutlineIcon sx={{ marginLeft: '5px' }} color='success' />
                            </Tooltip> 
                          }
                          {!hasStudyPlanAndAcademicReport && iconRedirectionToStep(STEPS.DOCUMENT_UPLOAD)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>{t('academic_report')}</TableCell>
                        <TableCell>
                          {(isExternal && !hasStudyPlanAndAcademicReport) ?
                            <Tooltip placement='top' arrow title={t('confirmationDialogCheckStudyPlanAndAcademicReportTitle')} data-testid='complete-academic-report-warning'>
                              <ErrorOutline sx={{ marginLeft: '5px' }} color='warning' />
                            </Tooltip>
                            :
                            <Tooltip placement='top' arrow title={t('confirmationDialogCheckAcademicReportTitleSuccess')} data-testid='complete-academic-report-success'>
                              <CheckCircleOutlineIcon sx={{ marginLeft: '5px' }} color='success' />
                            </Tooltip>
                          }
                          {!hasStudyPlanAndAcademicReport && iconRedirectionToStep(STEPS.DOCUMENT_UPLOAD)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>{t('syllabus')}</TableCell>
                        <TableCell>
                          {(isExternal && !hasStudyPlanAndAcademicReport) ?
                            <Tooltip placement='top' arrow title={t('confirmationDialogCheckStudyPlanAndAcademicReportTitle')} data-testid='complete-study-plan-warning'>
                              <ErrorOutline sx={{ marginLeft: '5px' }} color='warning' />
                            </Tooltip>
                            :
                            <Tooltip placement='top' arrow title={t('confirmationDialogCheckStudyPlanReportTitleSuccess')} data-testid='complete-study-plan-success'>
                              <CheckCircleOutlineIcon sx={{ marginLeft: '5px' }} color='success' />
                            </Tooltip>  
                          }
                          {!hasStudyPlanAndAcademicReport && iconRedirectionToStep(STEPS.DOCUMENT_UPLOAD)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                <div style={{ display: 'flex', alignItems: 'center', marginTop: '10px' }}>
                  <Typography color="#949494" sx={{ fontSize: '14px' }}>
                    {t('editLabelCapitalize') + ' ' + t('stepFiveName')}
                  </Typography>
                  {iconRedirectionToStep(STEPS.DOCUMENT_UPLOAD)}
                </div>
              </TabPanel>


            </TabContext>
          </Box>
          <Typography color="#949494" sx={{ fontSize: '14px', padding: '0px 24px 0px 24px' }}>
            {t('confirmationDialogExtraConfirmationTitle')}
          </Typography>
        </div>
      </DialogContent>
      <DialogActions sx={{ paddingRight: '20px' }}>
        <Button
          onClick={() => onClose(false)}
          color="primary"
          id="cancel-button-confirm-request"
          data-testid="cancel-button-confirm-request"
          sx={{ marginRight: '5px' }}
        >
          {t('cancelLabel')}
        </Button>
        <Tooltip
          title={thirdStepValidation ?  '' : t('mandatoryDataNeeded')}
          placement="top"
        >
          <span>
            <Button
              onClick={() => onClose(true)}
              color="primary"
              variant="contained"
              id="confirm-button-confirm-request"
              data-testid="confirm-button-confirm-request"
              disabled={!thirdStepValidation}
            >
              {t('confirm')}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationModal;
